import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  RiCloseLine,
  RiArrowUpLine,
  RiArrowDownLine,
  RiArrowLeftLine,
  RiArrowRightLine,
} from '@remixicon/react';
// import { Entrance_Animations, Continuous_Animations, ANIMATION_CONFIGS } from '../SlideAnimations';
import { Entrance_Animations, Continuous_Animations, ANIMATION_CONFIGS } from '../slideAnimations';
const toLabel = (keyOrType) => {
  const key = String(keyOrType)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ');
  return key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
};

const buildDefaultAnimation = (type, category) => {
  const baseConfig = ANIMATION_CONFIGS[type] || {};

  return {
    type,
    category,
    duration: baseConfig.duration || 1000,
    delay: baseConfig.delay || 0,
    easing: 'ease-in-out',
    direction: 'none',
    translateXFrom: 0,
    translateXTo: 0,
    translateYFrom: 0,
    translateYTo: 0,
    scaleFrom: 1,
    scaleTo: 1,
    opacityFrom: 1,
    opacityTo: 1,
    rotationFrom: 0,
    rotationTo: 0,
  };
};

const AnimationModal = ({ open, onClose, initialAnimation, onSave }) => {
  const [tab, setTab] = useState(initialAnimation?.category === 'continuous' ? 'continuous' : 'entrance');

  const entranceOptions = useMemo(
    () => Object.values(Entrance_Animations || {}),
    []
  );
  const continuousOptions = useMemo(
    () => Object.values(Continuous_Animations || {}),
    []
  );

  const [selectedAnimation, setSelectedAnimation] = useState(() => {
    if (initialAnimation) return initialAnimation;
    const firstType = entranceOptions[0] || null;
    return firstType ? buildDefaultAnimation(firstType, 'entrance') : null;
  });

  const handleSelectType = (type, category) => {
    setSelectedAnimation((prev) => {
      if (!prev || prev.type !== type) {
        return {
          ...(buildDefaultAnimation(type, category)),
          id: prev?.id,
        };
      }
      return prev;
    });
  };

  const handleFieldChange = (field, value) => {
    setSelectedAnimation((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNumberFieldChange = (field, value) => {
    const numeric = value === '' ? '' : Number(value);
    setSelectedAnimation((prev) => ({
      ...prev,
      [field]: isNaN(numeric) ? 0 : numeric,
    }));
  };

  const handleApply = () => {
    if (!selectedAnimation) return;
    onSave?.(selectedAnimation);
    onClose?.();
  };

  const directionButtons = [
    { value: 'none', label: 'None' },
    { value: 'up', icon: <RiArrowUpLine size={18} /> },
    { value: 'down', icon: <RiArrowDownLine size={18} /> },
    { value: 'left', icon: <RiArrowLeftLine size={18} /> },
    { value: 'right', icon: <RiArrowRightLine size={18} /> },
  ];

  const renderTypeGrid = (options, category) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 1,
        mb: 2,
      }}
    >
      {options.map((type) => {
        const isActive = selectedAnimation?.type === type;
        return (
          <Button
            key={type}
            variant={isActive ? 'contained' : 'outlined'}
            onClick={() => handleSelectType(type, category)}
            sx={{
              textTransform: 'none',
              fontSize: 12,
              justifyContent: 'flex-start',
              alignItems: 'center',
              px: 1.5,
              py: 1,
              borderRadius: 2,
              minHeight: 56,
              backgroundColor: isActive ? '#6B22D5' : '#F7F7F8',
              color: isActive ? '#FFFFFF' : '#111827',
              borderColor: isActive ? '#6B22D5' : '#E5E7EB',
              '&:hover': {
                backgroundColor: isActive ? '#5B1DB7' : '#EEEFF2',
                borderColor: '#6B22D5',
              },
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: 1.5,
                backgroundColor: isActive ? '#7C3AED' : '#FFFFFF',
                mr: 1,
              }}
            />
            <Typography variant="body2">{toLabel(type)}</Typography>
          </Button>
        );
      })}
    </Box>
  );

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        right: 320,
        top: 64,
        width: 360,
        height: 'calc(100vh - 64px)',
        backgroundColor: 'white',
        borderLeft: '1px solid #E0E0E0',
        boxShadow: '-4px 0 12px rgba(0,0,0,0.1)',
        zIndex: 999,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2,
          borderBottom: '1px solid #E0E0E0',
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Animation
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <RiCloseLine size={18} />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="fullWidth"
        sx={{
          minHeight: 40,
          borderBottom: '1px solid #E0E0E0',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: 13,
            minHeight: 40,
          },
        }}
      >
        <Tab label="Entrance" value="entrance" />
        <Tab label="Continues" value="continuous" />
      </Tabs>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {tab === 'entrance'
          ? renderTypeGrid(entranceOptions, 'entrance')
          : renderTypeGrid(continuousOptions, 'continuous')}

        <Divider sx={{ my: 2 }} />

        {/* Direction */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Direction
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            {directionButtons.map((btn) => {
              const isActive = selectedAnimation?.direction === btn.value;
              return (
                <Button
                  key={btn.value}
                  size="small"
                  variant={isActive ? 'contained' : 'outlined'}
                  onClick={() => handleFieldChange('direction', btn.value)}
                  sx={{
                    minWidth: 32,
                    px: 1,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: 11,
                  }}
                >
                  {btn.icon || btn.label}
                </Button>
              );
            })}
          </Box>
        </Box>

        {/* Timing */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Delay (ms)
            </Typography>
            <TextField
              size="small"
              type="number"
              value={selectedAnimation?.delay ?? 0}
              onChange={(e) => handleNumberFieldChange('delay', e.target.value)}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Duration (ms)
            </Typography>
            <TextField
              size="small"
              type="number"
              value={selectedAnimation?.duration ?? 1000}
              onChange={(e) => handleNumberFieldChange('duration', e.target.value)}
              fullWidth
            />
          </Box>
        </Box>

        {/* Transform */}
        <Typography variant="caption" color="text.secondary">
          Transform
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 1.5 }}>
          <TextField
            label="X From"
            size="small"
            type="number"
            value={selectedAnimation?.translateXFrom ?? 0}
            onChange={(e) => handleNumberFieldChange('translateXFrom', e.target.value)}
            fullWidth
          />
          <TextField
            label="X To"
            size="small"
            type="number"
            value={selectedAnimation?.translateXTo ?? 0}
            onChange={(e) => handleNumberFieldChange('translateXTo', e.target.value)}
            fullWidth
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField
            label="Y From"
            size="small"
            type="number"
            value={selectedAnimation?.translateYFrom ?? 0}
            onChange={(e) => handleNumberFieldChange('translateYFrom', e.target.value)}
            fullWidth
          />
          <TextField
            label="Y To"
            size="small"
            type="number"
            value={selectedAnimation?.translateYTo ?? 0}
            onChange={(e) => handleNumberFieldChange('translateYTo', e.target.value)}
            fullWidth
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField
            label="Scale From"
            size="small"
            type="number"
            value={selectedAnimation?.scaleFrom ?? 1}
            onChange={(e) => handleNumberFieldChange('scaleFrom', e.target.value)}
            fullWidth
          />
          <TextField
            label="Scale To"
            size="small"
            type="number"
            value={selectedAnimation?.scaleTo ?? 1}
            onChange={(e) => handleNumberFieldChange('scaleTo', e.target.value)}
            fullWidth
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField
            label="Opacity From"
            size="small"
            type="number"
            inputProps={{ min: 0, max: 1, step: 0.1 }}
            value={selectedAnimation?.opacityFrom ?? 1}
            onChange={(e) => handleNumberFieldChange('opacityFrom', e.target.value)}
            fullWidth
          />
          <TextField
            label="Opacity To"
            size="small"
            type="number"
            inputProps={{ min: 0, max: 1, step: 0.1 }}
            value={selectedAnimation?.opacityTo ?? 1}
            onChange={(e) => handleNumberFieldChange('opacityTo', e.target.value)}
            fullWidth
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            label="Rotation From"
            size="small"
            type="number"
            value={selectedAnimation?.rotationFrom ?? 0}
            onChange={(e) => handleNumberFieldChange('rotationFrom', e.target.value)}
            fullWidth
          />
          <TextField
            label="Rotation To"
            size="small"
            type="number"
            value={selectedAnimation?.rotationTo ?? 0}
            onChange={(e) => handleNumberFieldChange('rotationTo', e.target.value)}
            fullWidth
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            onClick={handleApply}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AnimationModal;


